import { action, makeObservable, observable } from 'mobx';

import { ILocalStore } from 'stores/interfaces';

import { TableFactoryRowModel } from '../TableFactoryRowModel';
import { ITableFactoryStore } from '../TableFactoryStore/interface';
import { TableRowDataStore } from '../TableRowDataStore';

type PrivateFields = '_cache' | '_visibleRowIds';

const CLEANUP_DELAY_MS = 5000;

/**
 * Cache for TableFactoryRowModel instances.
 * Creates models lazily when needed and cleans up non-visible ones.
 */
class RowModelCache implements ILocalStore {
  constructor(
    private readonly _dataStore: TableRowDataStore,
    private readonly _factoryStore: ITableFactoryStore
  ) {
    makeObservable<RowModelCache, PrivateFields>(this, {
      _cache: observable.ref,
      _visibleRowIds: observable.ref,
      setVisibleRows: action.bound,
    });
  }

  private _cache: Map<string, TableFactoryRowModel> = new Map();
  private _visibleRowIds: Set<string> = new Set();
  private _cleanupTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Get a row model from cache or create a new one.
   * This is the main entry point for lazy model creation.
   */
  getOrCreate(rowId: string): TableFactoryRowModel | null {
    const cached = this._cache.get(rowId);

    if (cached) {
      return cached;
    }

    const entity = this._dataStore.getRowEntity(rowId);

    if (!entity) {
      return null;
    }

    const model = new TableFactoryRowModel({
      entity,
      parent: null,
      formTableStore: this._factoryStore,
    });

    this._cache.set(rowId, model);

    return model;
  }

  /**
   * Get a cached model without creating a new one.
   * Used for updates to check if model exists in cache.
   */
  get(rowId: string): TableFactoryRowModel | null {
    return this._cache.get(rowId) ?? null;
  }

  /**
   * Check if a model is cached.
   */
  has(rowId: string): boolean {
    return this._cache.has(rowId);
  }

  /**
   * Set the currently visible row IDs.
   * Non-visible rows will be cleaned up after a delay.
   */
  setVisibleRows(rowIds: string[]): void {
    this._visibleRowIds = new Set(rowIds);
    this._scheduleCleanup();
  }

  /**
   * Schedule cleanup of non-visible row models.
   * Uses a delay to avoid cleanup during fast scrolling.
   */
  private _scheduleCleanup(): void {
    if (this._cleanupTimer) {
      return;
    }

    this._cleanupTimer = setTimeout(() => {
      this._cleanupTimer = null;
      this._cleanup();
    }, CLEANUP_DELAY_MS);
  }

  /**
   * Clean up row models that are no longer visible.
   */
  private _cleanup(): void {
    const newCache = new Map<string, TableFactoryRowModel>();

    for (const [rowId, model] of this._cache) {
      if (this._visibleRowIds.has(rowId)) {
        newCache.set(rowId, model);
      } else {
        model.destroy();
      }
    }

    this._cache = newCache;
  }

  /**
   * Clear all cached models.
   */
  clear(): void {
    if (this._cleanupTimer) {
      clearTimeout(this._cleanupTimer);
      this._cleanupTimer = null;
    }

    this._cache.forEach((model) => model.destroy());
    this._cache = new Map();
    this._visibleRowIds = new Set();
  }

  destroy(): void {
    this.clear();
  }
}

export default RowModelCache;
